import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExportWizardState } from "./ExportWizardState";
import { EstimateState } from "./useFilterEstimate";
import {
  CostRates,
  costForSearchEstimate,
  formatCost,
  formulaLinesForSearchEstimate,
} from "../api/cost";
import { describeDataset } from "../api/aiDescribe";
import { CostFormulaPre } from "../components/searchEnv/CostFormulaPre";

interface Props {
  state: ExportWizardState;
  bucket: string;
  // Filter estimate, owned by the wizard. Should already be kicked
  // by the time the user reaches Review (we auto-kick on Next from
  // the Filter step). If not, the user is invited back.
  estimate: EstimateState;
  estimateStale: boolean;
  // Fargate $/hr rates from /api/me. costForSearchEstimate combines
  // these with the Fargate slot shape (16 vCPU + 32 GiB) and a
  // throughput model to project a per-slot wall-time cost.
  costRates: CostRates;
  // True when the portal has the AI feature enabled (OPENAI_API_KEY
  // configured). Drives the auto-fill of the dataset description.
  aiEnabled: boolean;
  // Wizard state mutator for the description textfield (Review owns
  // both the input UI and the AI auto-fill side effect).
  onDescriptionChange: (description: string) => void;
  onSubmit: () => Promise<void>;
  submitting: boolean;
  submitError: string | null;
}

// Step 5 — review + submit.
//
// Reads the post-filter document count from the wizard-level estimate
// (the same one the funnel breadcrumb shows) so we don't kick a second
// count call. Displays the estimated total cost — search-cost rate ×
// (filter-matched docs / 1000) — so the user sees a $-figure before
// clicking Submit.
//
// "Stale" estimate: if the user changed the filter or month selection
// after kicking the last estimate, we surface a warning so they don't
// commit against a stale number.

const fmt = (n: number) => n.toLocaleString();

export function StepReview({
  state,
  bucket,
  estimate,
  estimateStale,
  costRates,
  aiEnabled,
  onDescriptionChange,
  onSubmit,
  submitting,
  submitError,
}: Props) {
  const [costExpanded, setCostExpanded] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const aiTriedRef = useRef(false);

  const runAiFill = (manual: boolean) => {
    setAiBusy(true);
    setAiError(null);
    describeDataset({
      months: state.months,
      filter: state.filter,
      search: state.search,
      output_format: state.outputFormat,
    })
      .then((r) => {
        onDescriptionChange(r.description);
      })
      .catch((e) => {
        // Silent on the auto-fill (the field just stays empty);
        // surface only when the user explicitly clicked the button.
        if (manual) setAiError(e?.message ?? "AI description failed");
      })
      .finally(() => setAiBusy(false));
  };

  // First-visit auto-fill: when AI is enabled, the user hasn't typed
  // anything yet, and we haven't already kicked the call this mount.
  // Re-triggering on inputs is intentionally off — re-running on every
  // keystroke would clobber the user's edits.
  useEffect(() => {
    if (!aiEnabled) return;
    if (state.description.trim()) return;
    if (aiTriedRef.current) return;
    aiTriedRef.current = true;
    runAiFill(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiEnabled]);
  const ok = estimate.kind === "ok" ? estimate : null;
  const cost = ok
    ? costForSearchEstimate(ok.total, state.months.length, costRates)
    : null;
  const formulaLines = ok
    ? formulaLinesForSearchEstimate(
        ok.total,
        state.months.length,
        costRates,
      )
    : null;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review your export. The headline below shows the post-filter document
        count and the predicted cost; the optional Lucene search runs at export
        time and will whittle further (but always returns &lt;= the filter count).
      </Typography>

      <Stack spacing={2}>
        {/* Headline: scan N docs · est. cost $X */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderColor: ok ? "primary.main" : "divider",
            borderWidth: ok ? 2 : 1,
          }}
        >
          {estimate.kind === "idle" && (
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                No estimate yet — go back to the Filter step and click
                <strong> Estimate matches</strong> to see the predicted scope.
              </Typography>
            </Stack>
          )}
          {estimate.kind === "running" && (
            <Stack direction="row" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography variant="body1">Estimating matches…</Typography>
            </Stack>
          )}
          {estimate.kind === "error" && (
            <Alert severity="error">
              Estimate failed: {estimate.message}. Go back to the Filter step
              and try again.
            </Alert>
          )}
          {ok && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="overline" color="text.secondary">
                  Pre-flight summary
                </Typography>
                {formulaLines && (
                  <IconButton
                    size="small"
                    onClick={() => setCostExpanded((v) => !v)}
                    aria-label={
                      costExpanded
                        ? "Hide cost justification"
                        : "Show cost justification"
                    }
                    sx={{ p: 0.25 }}
                  >
                    {costExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Stack>
              <Typography variant="h4" sx={{ mt: 0.5 }}>
                Scan {fmt(ok.total)} documents
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
                Estimated cost: {formatCost(cost ?? 0)}
                {state.search.trim() && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (Lucene search may reduce the kept set further at export
                    time)
                  </Typography>
                )}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1.5 }}
              >
                Estimate models Fargate compute time: docs / per-slot
                throughput (16 vCPU @ ~70k docs/sec), one slot per month,
                floored at the 60s billing minimum. Doesn't account for the
                optional search filter reducing the kept count.
              </Typography>
              {costExpanded && formulaLines && (
                <CostFormulaPre formulaLines={formulaLines} />
              )}
              {estimateStale && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This estimate was for a different filter or month set. Go
                  back and re-estimate before submitting.
                </Alert>
              )}
              {ok.missingMonths.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Missing rollups for: {ok.missingMonths.join(", ")} — those
                  months won't contribute to the count.
                </Alert>
              )}
            </Box>
          )}
        </Paper>

        {/* Inputs (collapsed reference) */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Inputs
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {state.months.map((m) => (
              <Chip key={m} size="small" label={m} />
            ))}
          </Box>
          <Typography variant="caption" component="div" sx={{ mt: 1 }}>
            <strong>Bucket</strong> (read): <code>{bucket}</code>
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Filter
          </Typography>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {state.filter || "(none)"}
          </pre>
        </Paper>

        {state.search && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Search (Lucene)
            </Typography>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {state.search}
            </pre>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Output
          </Typography>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{state.output}</pre>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            format: <strong>{state.outputFormat}</strong>
            {state.outputFormat === "condensed-json" &&
              " — enables the search-env analysis surface once the export completes"}
          </Typography>
        </Paper>

        {/* Per-month breakdown — only when we have a fresh estimate. */}
        {ok && Object.keys(ok.byMonth).length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Per-month breakdown
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>month</TableCell>
                  <TableCell align="right">matches</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(ok.byMonth)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([month, count]) => (
                    <TableRow key={month}>
                      <TableCell>{month}</TableCell>
                      <TableCell align="right">{fmt(count)}</TableCell>
                    </TableRow>
                  ))}
                <TableRow>
                  <TableCell>
                    <strong>total</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{fmt(ok.total)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Description
            </Typography>
            {aiEnabled && (
              <Tooltip
                title={
                  aiBusy
                    ? "Generating description…"
                    : "Re-generate a description from your filter, search, and months"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => runAiFill(true)}
                    disabled={aiBusy}
                    aria-label="Generate description with AI"
                  >
                    {aiBusy ? (
                      <CircularProgress size={14} />
                    ) : (
                      <AutoFixHighIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            One or two sentences describing what's in this dataset.
            {aiEnabled && " A draft is filled in automatically — edit before submitting."}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            placeholder="e.g. Cable-news prime-time mentions of interest-rate cuts, Apr–Jun 2025."
            value={state.description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            disabled={submitting}
          />
          {aiError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {aiError}
            </Alert>
          )}
        </Paper>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit export"}
        </Button>
      </Stack>
    </Box>
  );
}
